import React, { useState, useEffect, useCallback, useRef, FormEvent } from 'react';
import { apiGet, apiPatch, apiPost, apiPut, apiDelete } from '../api/client';
import type {
  SecureNoteListResponse,
  SecureNoteResponse,
  CreateSecureNoteRequest,
  UpdateSecureNoteRequest,
} from '../types/api';
import { VaultRole } from '../types/api';
import AlertMessage from './AlertMessage';
import LoadingSpinner from './LoadingSpinner';
import Skeleton from './Skeleton';
import EmptyState from './EmptyState';
import SortableList from './SortableList';
import './NotesList.css';

interface Props {
  vaultId: string;
  userRole: VaultRole;
  onCountChange?: (count: number) => void;
}

export default function NotesList({ vaultId, userRole, onCountChange }: Props) {
  const onCountChangeRef = useRef(onCountChange);
  onCountChangeRef.current = onCountChange;

  const [notes, setNotes] = useState<SecureNoteResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedContent, setExpandedContent] = useState<string | null>(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState<SecureNoteResponse | null>(null);
  const [deleting, setDeleting] = useState(false);

  const canEdit = userRole === VaultRole.Owner || userRole === VaultRole.Spouse;

  const loadNotes = useCallback(async () => {
    try {
      const data = await apiGet<SecureNoteListResponse>(`/api/vault/${vaultId}/notes`);
      setNotes(data.notes);
      onCountChangeRef.current?.(data.notes.length);
    } catch (err: any) {
      setError(err.message || 'Failed to load notes.');
    } finally {
      setLoading(false);
    }
  }, [vaultId]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  async function handleReorder(reordered: SecureNoteResponse[]) {
    setNotes(reordered);
    try {
      await apiPatch(`/api/vault/${vaultId}/notes/reorder`, {
        orderedIds: reordered.map(n => n.id),
      });
    } catch {
      await loadNotes();
    }
  }

  async function handleExpand(noteId: string) {
    if (expandedId === noteId) {
      setExpandedId(null);
      setExpandedContent(null);
      setEditingId(null);
      return;
    }

    setExpandedId(noteId);
    setExpandedContent(null);
    setEditingId(null);
    setLoadingContent(true);

    try {
      const note = await apiGet<SecureNoteResponse>(`/api/vault/${vaultId}/notes/${noteId}`);
      setExpandedContent(note.content);
    } catch {
      setExpandedContent('[Unable to decrypt note]');
    } finally {
      setLoadingContent(false);
    }
  }

  function startEdit(note: SecureNoteResponse) {
    setEditingId(note.id);
    setEditTitle(note.title);
    setEditContent(expandedContent || '');
  }

  function cancelEdit() {
    setEditingId(null);
  }

  async function handleSaveEdit(e: FormEvent) {
    e.preventDefault();
    if (!editingId) return;
    setError('');
    setEditSaving(true);

    try {
      const body: UpdateSecureNoteRequest = { title: editTitle, content: editContent };
      await apiPut(`/api/vault/${vaultId}/notes/${editingId}`, body);
      setEditingId(null);
      setExpandedId(null);
      setExpandedContent(null);
      await loadNotes();
    } catch (err: any) {
      setError(err.message || 'Failed to update note.');
    } finally {
      setEditSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirmDelete) return;
    setDeleting(true);
    setError('');
    try {
      await apiDelete(`/api/vault/${vaultId}/notes/${confirmDelete.id}`);
      setConfirmDelete(null);
      if (expandedId === confirmDelete.id) {
        setExpandedId(null);
        setExpandedContent(null);
        setEditingId(null);
      }
      await loadNotes();
    } catch (err: any) {
      setError(err.message || 'Delete failed.');
      setConfirmDelete(null);
    } finally {
      setDeleting(false);
    }
  }

  async function handleAddNote(e: FormEvent) {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const body: CreateSecureNoteRequest = { title, content };
      await apiPost(`/api/vault/${vaultId}/notes`, body);
      setTitle('');
      setContent('');
      setShowForm(false);
      await loadNotes();
    } catch (err: any) {
      setError(err.message || 'Failed to save note.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Skeleton variant="row" count={3} />;

  return (
    <div>
      <div className="notes-header">
        <h3>Private Notes</h3>
        <button
          className="btn-text"
          onClick={() => canEdit && setShowForm(!showForm)}
          disabled={!canEdit}
        >
          {showForm ? 'Cancel' : '+ Add Note'}
        </button>
      </div>

      <p className="notes-description">
        Note content is encrypted before storage &mdash; your private space for passwords, PINs,
        and anything you're not ready to share with the whole family.{' '}
        <span className="notes-encryption-detail" title="Unlike documents, private note content is encrypted at the application layer before it reaches our database. Cedar Vault staff with database access cannot read your notes.">
          What makes this different?
        </span>
      </p>

      <div className="notes-private-callout">
        <span className="notes-private-icon" aria-hidden="true">🔒</span>
        <span>Visible only to you and your spouse — Witnesses and Advisors do not have access.</span>
      </div>

      <AlertMessage type="error" message={error} />

      {showForm && (
        <form className="note-form" onSubmit={handleAddNote}>
          <div className="form-group">
            <label className="form-label" htmlFor="noteTitle">Title</label>
            <input
              id="noteTitle"
              className="form-input"
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Note title"
              required
              maxLength={200}
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="noteContent">Content</label>
            <textarea
              id="noteContent"
              className="form-input"
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder="Enter your private note..."
              required
            />
          </div>
          <div className="note-form-actions">
            <button className="btn-secondary" type="button" onClick={() => setShowForm(false)}>
              Cancel
            </button>
            <button className="btn-primary" type="submit" disabled={saving}>
              {saving ? <LoadingSpinner small /> : 'Save Note'}
            </button>
          </div>
        </form>
      )}

      {notes.length === 0 ? (
        <EmptyState
          icon="notes"
          title="No private notes yet"
          subtitle="Click '+ Add Note' to store your first encrypted note."
        />
      ) : (
        <div className="notes-list">
          <SortableList
            items={notes}
            canReorder={canEdit}
            onReorder={handleReorder}
            renderItem={(note) => (
            <div className={`note-item${expandedId === note.id ? ' note-item--expanded' : ''}`}>
              <div className="note-item-header" onClick={() => handleExpand(note.id)}>
                <span className="note-item-title">{note.title}</span>
                <span className="note-item-chevron">{expandedId === note.id ? '▲' : '▼'}</span>
              </div>
              {expandedId === note.id && (
                <div className="note-item-body">
                  {loadingContent ? (
                    <div className="note-item-content"><LoadingSpinner small /></div>
                  ) : editingId === note.id ? (
                    <form className="note-edit-form" onSubmit={handleSaveEdit}>
                      <div className="form-group">
                        <label className="form-label" htmlFor="editTitle">Title</label>
                        <input
                          id="editTitle"
                          className="form-input"
                          type="text"
                          value={editTitle}
                          onChange={e => setEditTitle(e.target.value)}
                          required
                          maxLength={200}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="editContent">Content</label>
                        <textarea
                          id="editContent"
                          className="form-input"
                          value={editContent}
                          onChange={e => setEditContent(e.target.value)}
                          required
                        />
                      </div>
                      <div className="note-form-actions">
                        <button className="btn-secondary" type="button" onClick={cancelEdit} disabled={editSaving}>
                          Cancel
                        </button>
                        <button className="btn-primary" type="submit" disabled={editSaving}>
                          {editSaving ? <LoadingSpinner small /> : 'Save Changes'}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <div className="note-item-content">
                        {expandedContent || ''}
                      </div>
                      <div className="note-item-actions">
                        <button className="note-edit-btn" onClick={() => startEdit(note)} disabled={!canEdit}>
                          Edit
                        </button>
                        <button className="note-delete-btn" onClick={() => setConfirmDelete(note)} disabled={!canEdit}>
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            )}
          />
        </div>
      )}

      {confirmDelete && (
        <div className="note-confirm-overlay" onClick={() => !deleting && setConfirmDelete(null)}>
          <div className="note-confirm-dialog" onClick={e => e.stopPropagation()}>
            <h4>Delete Private Note</h4>
            <p>
              Are you sure you want to delete <strong>{confirmDelete.title}</strong>?
              This action cannot be undone.
            </p>
            <div className="note-confirm-actions">
              <button
                className="note-confirm-delete"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
              <button
                className="note-confirm-cancel"
                onClick={() => setConfirmDelete(null)}
                disabled={deleting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
